import { useState, FormEvent, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { verify2FA, ApiException } from '../api/client';
import { useAuth } from '../App';

/**
 * 2FA Verify Page
 *
 * Gute Code-Input UX:
 * - 6 einzelne Felder
 * - Auto-Focus auf nächstes Feld
 * - Paste Support
 */
// Security: Nur interne Pfade erlauben (kein Open Redirect)
function getSafeReturnTo(returnTo: string | null): string {
  if (!returnTo) return '/dashboard';
  // Muss mit / beginnen und darf nicht mit // oder http beginnen
  if (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.toLowerCase().startsWith('/http')) {
    return '/dashboard';
  }
  return returnTo;
}

export default function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  
  // returnTo aus URL Query Parameter
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Nur Zahlen erlauben
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus nächstes Feld
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit wenn vollständig
    if (newCode.every((d) => d) && newCode.join('').length === 6) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Backspace: vorheriges Feld fokussieren
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('');

    if (codeToVerify.length !== 6) {
      setError('Bitte geben Sie den 6-stelligen Code ein');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await verify2FA(codeToVerify);
      // WICHTIG: AuthContext refreshen bevor Navigation
      await refresh();
      // Zu returnTo navigieren (oder /dashboard als Fallback)
      navigate(returnTo);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error || 'Ungültiger Code');
      } else {
        setError('Verbindungsfehler');
      }
      // Code zurücksetzen
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kiosk-container bg-gradient-to-br from-blue-50 to-gray-100">
      <div className="kiosk-card">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Zwei-Faktor-Authentifizierung</h1>
          <p className="text-gray-500 mt-2">
            Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Code Input */}
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="space-y-6"
        >
          <div className="flex justify-center gap-3" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                disabled={loading}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={loading || code.some((d) => !d)}
            className="btn btn-primary w-full flex items-center justify-center"
          >
            {loading ? (
              <>
                <span className="spinner mr-2" />
                Verifizieren...
              </>
            ) : (
              'Verifizieren'
            )}
          </button>
        </form>

        {/* Help */}
        <p className="mt-6 text-center text-sm text-gray-500">
          Öffnen Sie Ihre Authenticator-App (z.B. Google Authenticator) und geben Sie den angezeigten Code ein.
        </p>
      </div>
    </div>
  );
}
